import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import { ListingCard } from "@/components/listings/ListingCard";
import type { Game, Wishlist } from "@/types";
import { usePageTitle } from "@/hooks/usePageTitle";
import { RatingBadge } from "@/components/ui/RatingBadge";
import { GameImage } from "@/components/games/GameImage";
import { getShopLogoUrl } from "@/utils/shopLogos";
import { shopClickUrl } from "@/lib/shopClickUrl";
import { lastCheckedLabel } from "@/lib/lastChecked";
import { PriceHistory } from "@/components/games/PriceHistory";
import { PriceHeroStrip } from "@/components/games/PriceHeroStrip";
import { RatingContextBadge } from "@/components/games/RatingContextBadge";
import { GameSocialProof } from "@/components/games/GameSocialProof";
import { SimilarGamesStrip } from "@/components/games/SimilarGamesStrip";
import { PriceDropAlertButton } from "@/components/games/PriceDropAlertButton";

const GAME_QUERY = gql`
  query Game($id: ID!) {
    game(id: $id) {
      id
      slug
      title
      description
      imageUrl
      thumbnailUrl
      yearPublished
      minPlayers
      maxPlayers
      playerRange
      minPlaytime
      maxPlaytime
      playtimeRange
      minAge
      complexity
      complexityLabel
      bggRating
      displayRating
      ratingSource
      bggId
      designer
      publisher
      mechanics
      translatedMechanics
      genres
      translatedGenres
      categories
      activeListingsCount
      lowestPrice
      wishlistsCount
      latestListingAt
      listings {
        id
        price
        condition
        conditionLabel
        status
        createdAt
        imageUrls
        displayTitle
        displayImageUrl
        shippingAvailable
        shippingCost
        location
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
        siblingListings {
          id
          status
          gameTitle
          price
          boundingBox {
            yMin
            xMin
            yMax
            xMax
          }
        }
        game {
          id
          title
          imageUrl
          thumbnailUrl
          bggRating
          displayRating
          ratingSource
        }
        seller {
          id
          displayName
          avatarUrl
          rating
          ratingsCount
        }
      }
      shopListings {
        id
        shopName
        listingUrl
        price
        priceUpdatedAt
        inStock
        stockCheckedAt
        updatedAt
      }
      priceHistory {
        date
        price
        source
        shopName
        condition
        listingUrl
        listingName
        editionHint
      }
      priceEnvelope {
        date
        min
        med
        max
        shops {
          shopName
          price
          listingUrl
          listingName
        }
      }
      priceTrackingSince
      similarGames {
        id
        slug
        title
        imageUrl
        thumbnailUrl
        displayRating
        ratingSource
        lowestPrice
        activeListingsCount
      }
      myWishlist {
        id
        maxPrice
        notifyOnPriceDrop
      }
    }
  }
`;

const WISHLISTS_QUERY = gql`
  query Wishlists {
    wishlists {
      id
      game {
        id
      }
    }
  }
`;

const TOGGLE_WISHLIST_MUTATION = gql`
  mutation ToggleWishlist($gameId: ID!) {
    toggleWishlist(gameId: $gameId) {
      wishlist {
        id
      }
      removed
    }
  }
`;

function formatIsoDate(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const DESCRIPTION_PREVIEW_CHARS = 280;

export function GameDetailPage() {
  const { t } = useTranslation();
  const { id: rawId } = useParams<{ id: string }>();
  const id = rawId?.split("-")[0];
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [descExpanded, setDescExpanded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["game", id],
    queryFn: () =>
      graphqlClient.request<{ game: Game | null }>(GAME_QUERY, { id }),
    enabled: !!id,
  });

  const { data: wishlistData } = useQuery({
    queryKey: ["wishlists"],
    queryFn: () =>
      graphqlClient.request<{ wishlists: Wishlist[] }>(WISHLISTS_QUERY),
    enabled: !!user,
  });

  const toggleWishlist = useMutation({
    mutationFn: () =>
      graphqlClient.request(TOGGLE_WISHLIST_MUTATION, { gameId: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wishlists"] });
      queryClient.invalidateQueries({ queryKey: ["game", id] });
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
    },
  });

  const game = data?.game;

  useEffect(() => {
    if (!game?.slug || !rawId) return;
    const canonical = `${game.id}-${game.slug}`;
    if (rawId !== canonical) {
      navigate(`/games/${canonical}`, { replace: true });
    }
  }, [game?.id, game?.slug, rawId, navigate]);

  usePageTitle(game?.title);

  const isWishlisted = wishlistData?.wishlists.some(
    (w) => w.game.id === id
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-800 border-t-transparent" />
      </div>
    );
  }

  if (!game) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg text-gray-500">{t("game_detail.not_found")}</p>
        <Link
          to="/games"
          className="mt-4 inline-block text-sm font-medium text-primary-800 hover:text-primary-700"
        >
          {t("game_detail.back_to_games")}
        </Link>
      </div>
    );
  }

  const primaryGenreRaw = game.genres?.[0] ?? null;
  const primaryGenreLabel = game.translatedGenres?.[0] ?? primaryGenreRaw;

  const description = game.description ?? "";
  const plainDescription = description.replace(/<[^>]*>/g, "");
  const isLong = plainDescription.length > DESCRIPTION_PREVIEW_CHARS;
  const descriptionToShow =
    !isLong || descExpanded
      ? description
      : `${plainDescription.slice(0, DESCRIPTION_PREVIEW_CHARS).trim()}…`;

  const handleWishlistClick = () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    toggleWishlist.mutate();
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <nav className="mb-6 text-sm text-gray-500">
        <Link to="/games" className="hover:text-primary-800">
          {t("game_detail.breadcrumb_games")}
        </Link>
        {primaryGenreRaw && primaryGenreLabel && (
          <>
            <span className="mx-2">/</span>
            <Link
              to={`/games?genre=${encodeURIComponent(primaryGenreRaw)}`}
              className="hover:text-primary-800"
            >
              {primaryGenreLabel}
            </Link>
          </>
        )}
        <span className="mx-2">/</span>
        <span className="text-gray-900">{game.title}</span>
      </nav>

      {/* Game header */}
      <div className="grid gap-8 lg:grid-cols-5">
        {/* Image */}
        <div className="lg:col-span-2">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
            <GameImage
              src={game.imageUrl}
              alt={game.title}
              className="h-full w-full object-cover"
              placeholderClassName="aspect-square"
            />
          </div>

          {/* Wishlist (primary, full-width on mobile) + price-alert (secondary).
              On mobile the buttons stack vertically with wishlist on top so the
              dominant action — "track this game" — is the obvious one. */}
          <div className="mt-4 flex flex-col gap-2 sm:grid sm:grid-cols-2">
            <button
              onClick={handleWishlistClick}
              disabled={toggleWishlist.isPending}
              title={t("game_detail.wishlist_tooltip")}
              className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                isWishlisted
                  ? "bg-red-50 text-red-700 hover:bg-red-100"
                  : "bg-primary-800 text-white hover:bg-primary-700"
              }`}
            >
              <svg
                className="h-5 w-5"
                fill={isWishlisted ? "currentColor" : "none"}
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              {isWishlisted ? t("game_detail.in_wishlist") : t("game_detail.add_to_wishlist")}
            </button>
            <PriceDropAlertButton
              gameId={game.id}
              shopListings={game.shopListings}
              currentTargetPrice={game.myWishlist?.notifyOnPriceDrop ? game.myWishlist?.maxPrice ?? null : null}
              alertEnabled={!!game.myWishlist?.notifyOnPriceDrop}
            />
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {game.title}
              </h1>
              {game.yearPublished && (
                <p className="mt-1 text-lg text-gray-500">
                  {game.yearPublished}
                </p>
              )}
            </div>

            <div className="flex flex-col items-end gap-2">
              {game.displayRating != null && (
                <RatingBadge rating={game.displayRating} source={game.ratingSource ?? null} size="lg" />
              )}
              {game.displayRating != null && (
                <RatingContextBadge rating={game.displayRating} />
              )}
            </div>
          </div>

          {/* Commerce strip — distinct visual cluster around shop / community prices */}
          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary-700">
              {t("game_detail.commerce_strip_title")}
            </p>
            <PriceHeroStrip listings={game.listings} shopListings={game.shopListings} />
          </div>

          {/* Social proof signals */}
          <GameSocialProof
            wishlistsCount={game.wishlistsCount}
            activeListingsCount={game.activeListingsCount}
            latestListingAt={game.latestListingAt}
          />

          {/* Gameplay strip — visually distinct from the commerce cluster (gray, label header) */}
          <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {t("game_detail.gameplay_strip_title")}
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {game.playerRange && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
                    {t("game_detail.info_players")}
                  </p>
                  <p className="mt-0.5 text-base font-semibold text-gray-900">
                    {game.playerRange}
                  </p>
                </div>
              )}

              {game.playtimeRange && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
                    {t("game_detail.info_time")}
                  </p>
                  <p className="mt-0.5 text-base font-semibold text-gray-900">
                    {game.playtimeRange}
                  </p>
                </div>
              )}

              {game.complexity != null && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
                    {t("game_detail.info_complexity")}
                  </p>
                  <p className="mt-0.5 text-base font-semibold text-gray-900">
                    {game.complexity.toFixed(1)}
                    {game.complexityLabel && (
                      <span className="ml-1 text-xs font-normal text-gray-500">
                        ({game.complexityLabel})
                      </span>
                    )}
                  </p>
                </div>
              )}

              {game.minAge != null && (
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
                    {t("game_detail.info_age")}
                  </p>
                  <p className="mt-0.5 text-base font-semibold text-gray-900">
                    {t("game_detail.info_age_value", { count: game.minAge })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Price history — primary price-tracker promise */}
      <PriceHistory
        points={game.priceHistory}
        envelope={game.priceEnvelope ?? []}
        shopListings={game.shopListings}
        trackingSince={game.priceTrackingSince}
      />

      {/* Shop offers — comparison table, the heart of the price tracker */}
      {game.shopListings.length > 0 && (
        <section className="mt-12">
          <div className="mb-6 flex items-center gap-2">
            <h2 className="text-2xl font-bold text-gray-900">
              {t("game_detail.shop_offers_heading")}
            </h2>
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
              {t("game_detail.badge_shop")}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {game.shopListings.map((listing) => (
              <a
                key={listing.id}
                href={shopClickUrl(listing.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 rounded-xl border border-gray-200 p-4 transition-colors hover:border-primary-200 hover:bg-primary-50/50"
              >
                {getShopLogoUrl(listing.shopName) ? (
                  <img
                    src={getShopLogoUrl(listing.shopName)!}
                    alt={listing.shopName}
                    className="h-12 w-12 rounded-lg object-contain"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100 text-gray-400">
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      />
                    </svg>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900">
                    {listing.shopName}
                  </p>
                  {listing.price != null && (
                    <p className="text-lg font-bold text-primary-800">
                      {listing.price.toFixed(2)} EUR
                    </p>
                  )}
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px]">
                    {listing.inStock != null && (
                      <span
                        className={`inline-flex items-center gap-1 font-medium ${
                          listing.inStock ? "text-emerald-700" : "text-gray-500"
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            listing.inStock ? "bg-emerald-500" : "bg-gray-400"
                          }`}
                        />
                        {listing.inStock
                          ? t("game_detail.shop_in_stock")
                          : t("game_detail.shop_out_of_stock")}
                      </span>
                    )}
                    {(() => {
                      const label = lastCheckedLabel(listing.stockCheckedAt, t);
                      return label ? (
                        <span className="text-gray-400">{label}</span>
                      ) : listing.priceUpdatedAt ? (
                        <span className="text-gray-400">
                          {t("game_detail.shop_price_updated", { date: formatIsoDate(listing.priceUpdatedAt) })}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </div>
                <svg
                  className="h-5 w-5 shrink-0 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Used listings — promoted as a parallel-track buy option to the shop
          offers above. Render up to 3 inline; deeper inventory gets a clear
          "see all" jump. */}
      <section className="mt-12">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
              {t("game_detail.used_listings_kicker")}
            </p>
            <h2 className="mt-1 flex items-center gap-2 text-2xl font-bold text-gray-900">
              {t("game_detail.used_listings_heading", { count: game.listings.length })}
            </h2>
          </div>
          <Link
            to={user ? "/listings/new" : `/login?next=${encodeURIComponent("/listings/new")}`}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t("game_detail.list_your_copy")}
          </Link>
        </div>
        {game.listings.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {game.listings.slice(0, 4).map((listing) => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
            {game.listings.length > 4 && (
              <div className="mt-6 text-center">
                <Link
                  to={`/listings?q=${encodeURIComponent(game.title)}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-800 hover:text-primary-700"
                >
                  {t("game_detail.view_all_listings")}
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 py-12 text-center">
            <p className="text-gray-500">{t("game_detail.no_listings")}</p>
            <p className="mt-1 text-sm text-gray-400">
              {t("game_detail.no_listings_hint")}
            </p>
          </div>
        )}
      </section>

      {/* About this game — description, BGG link, designer/publisher, mechanics, genres */}
      <section className="mt-12">
        <h2 className="mb-6 text-2xl font-bold text-gray-900">
          {t("game_detail.about_heading")}
        </h2>

        {description && (
          <div>
            <div
              className="leading-relaxed text-gray-700"
              dangerouslySetInnerHTML={{ __html: descriptionToShow }}
            />
            {isLong && (
              <button
                onClick={() => setDescExpanded((v) => !v)}
                className="mt-2 text-sm font-medium text-primary-800 hover:text-primary-700"
              >
                {descExpanded ? t("game_detail.show_less") : t("game_detail.show_more")}
              </button>
            )}
          </div>
        )}

        {game.bggId && (
          <div className="mt-6">
            <a
              href={`https://boardgamegeek.com/boardgame/${game.bggId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-700 hover:text-orange-800"
            >
              {t("game_detail.view_on_bgg")}
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        )}

        {(game.designer || game.publisher) && (
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm text-gray-600">
            {game.designer && (
              <p>
                <span className="font-medium text-gray-900">
                  {t("game_detail.designer_label")}
                </span>{" "}
                {game.designer}
              </p>
            )}
            {game.publisher && (
              <p>
                <span className="font-medium text-gray-900">
                  {t("game_detail.publisher_label")}
                </span>{" "}
                {game.publisher}
              </p>
            )}
          </div>
        )}

        {game.translatedMechanics && game.translatedMechanics.length > 0 && (
          <div className="mt-6">
            <h3 className="mb-2 text-sm font-medium text-gray-900">
              {t("game_detail.mechanics_heading")}
            </h3>
            <div className="flex flex-wrap gap-2">
              {game.translatedMechanics.map((label, i) => {
                const raw = game.mechanics?.[i] ?? label;
                return (
                  <Link
                    key={`${raw}-${i}`}
                    to={`/games?mechanic=${encodeURIComponent(raw)}`}
                    className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
                  >
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {game.translatedGenres && game.translatedGenres.length > 0 && (
          <div className="mt-4">
            <h3 className="mb-2 text-sm font-medium text-gray-900">
              {t("game_detail.genres_heading")}
            </h3>
            <div className="flex flex-wrap gap-2">
              {game.translatedGenres.map((label, i) => {
                const raw = game.genres?.[i] ?? label;
                return (
                  <Link
                    key={`${raw}-${i}`}
                    to={`/games?genre=${encodeURIComponent(raw)}`}
                    className="rounded-full bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700 transition-colors hover:bg-purple-100"
                  >
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Similar games */}
      {game.similarGames && game.similarGames.length > 0 && (
        <SimilarGamesStrip games={game.similarGames} />
      )}
    </div>
  );
}
